import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import CustomAlertModal from './components/CustomAlertModal';
import { SubjectType } from './types';
import { auth, db, rtdb, isFirebaseActive } from './services/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { initUserListener, handleRedirectResult, AUTH_REDIRECT_KEY } from './services/userService';
import { collection, query, orderBy, limit as firestoreLimit, onSnapshot, Query } from 'firebase/firestore';
import { syncPushSubscription, saveSubscriptionToFirestore } from './services/notificationService';
import { initNativePushNotifications } from './services/nativePushService';
import { isNativePlatform, isAppShell, isAndroidTwa, configureStatusBar, isIOS } from './utils/capacitorBridge';
import { syncStorePurchasesSilently } from './services/storeBilling';
import { Sentry } from './services/sentry';
import { consumeBack, useEdgeSwipeBack } from './utils/backHandler';
import PodcastMiniPlayer from './components/PodcastMiniPlayer';
import PomodoroWidget from './components/PomodoroWidget';
import { startPomodoroClock } from './services/pomodoro';
import type { PlanTarget } from './utils/studyPlanner';
import { installStudySignals, recordGameTime, startAppTimeTracker, isPlanPro } from './services/studyPlanService';
import { requestProScreen } from './utils/proRequest';

// TWA tespiti ilk yüklemede yapılmalı: App.tsx native_fcm_token parametresini URL'den sonradan siler
isAndroidTwa();

// Ekranlar ayrı parçalarda: landing sayfasını açan ziyaretçi uygulamanın (soru verileri, modallar) tamamını indirmesin.
// Uygulama kabuğunda ya da girişli kullanıcıda gereken parçalar hemen, paralel indirilmeye başlar (bekleme zinciri olmasın).
const loadLandingPage = () => import('./components/LandingPage');
const loadSubjectSelectionScreen = () => import('./components/SubjectSelectionScreen');
const loadMainApp = () => import('./MainApp');
const LandingPage = lazy(loadLandingPage);
const SubjectSelectionScreen = lazy(loadSubjectSelectionScreen);
const MainApp = lazy(loadMainApp);
// "Kim Bilir?" yarışması kendi sayfası: açıkken ders seçim ekranı (3D sahne, Cezeri balonu…) hiç çizilmez
const KimBilirScreen = lazy(() => import('./components/KimBilirScreen'));
const TgdBookletScreen = lazy(() => import('./components/TgdBookletScreen'));
const StudyPlanScreen = lazy(() => import('./components/StudyPlanScreen'));
try {
    if (isAppShell() || localStorage.getItem('kpss_is_logged_in') === 'true') {
        // Ön yükleme hatası burada yutulur: aynı hata lazy() render'ında AppErrorBoundary'ye düşer (çift Sentry kaydı olmasın)
        loadSubjectSelectionScreen().catch(() => {});
        loadMainApp().catch(() => {});
    } else {
        loadLandingPage().catch(() => {});
    }
} catch {
    // localStorage erişilemezse ilgili ekran render anında yüklenir
}

const ScreenFallback = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-[#0f172a]">
        <div className="loader" />
    </div>
);
import { ref as dbRef, set as dbSet, onDisconnect as dbOnDisconnect, onValue as dbOnValue, serverTimestamp as dbServerTimestamp } from 'firebase/database';

const App: React.FC = () => {
    // Açık "Kim Bilir?" sayfası (code: davet bağlantısındaki oda numarası)
    const [kimBilir, setKimBilir] = useState<{ code?: string } | null>(null);
    // Kim Bilir oturum süresi (çalışma planı oyun tercihleri)
    const kimBilirAt = useRef<number | null>(null);
    useEffect(() => {
        if (kimBilir && !kimBilirAt.current) kimBilirAt.current = Date.now();
        if (!kimBilir && kimBilirAt.current) { recordGameTime('kimbilir', (Date.now() - kimBilirAt.current) / 1000); kimBilirAt.current = null; }
    }, [kimBilir]);
    const openKimBilir = React.useCallback((code?: string) => setKimBilir({ code }), []);
    const closeKimBilir = React.useCallback(() => {
        setKimBilir(null);
        try {
            if (new URLSearchParams(window.location.search).has('kimbilir')) window.history.replaceState(window.history.state, '', window.location.pathname);
        } catch { /* yoksay */ }
    }, []);
    // Açık Türkiye Geneli Genel Deneme kitapçığı (ayrı sayfa, Kim Bilir gibi)
    const [booklet, setBooklet] = useState(false);
    // Çalışma Planım (ayrı sayfa)
    const [studyPlan, setStudyPlan] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState<SubjectType | null>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('kpss_last_selected_subject');
            const validSubjects = ['geography', 'history', 'citizenship', 'turkish', 'math'];
            if (saved && validSubjects.includes(saved)) {
                return saved as SubjectType;
            }
        }
        return null;
    });
    const [backToast, setBackToast] = useState<string | null>(null);
    const [showExitConfirmModal, setShowExitConfirmModal] = useState<boolean>(false);
    const showExitConfirmModalRef = useRef<boolean>(false);
    useEffect(() => {
        showExitConfirmModalRef.current = showExitConfirmModal;
    }, [showExitConfirmModal]);
    const selectedSubjectRef = useRef<SubjectType | null>(selectedSubject);
    useEffect(() => {
        selectedSubjectRef.current = selectedSubject;
    }, [selectedSubject]);
    const lastBackActionTimeRef = useRef<number>(0);
    const [user, setUser] = useState<any>(auth.currentUser);
    const [isLoggedInCached, setIsLoggedInCached] = useState<boolean>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('kpss_is_logged_in') === 'true' || !!auth.currentUser;
        }
        return false;
    });
    const [isAuthReady, setIsAuthReady] = useState(true);
    // Yönlendirmeli Google girişinden (Android/mobil web) dönüş: sonuç okunana kadar "Giriş yapılıyor" (eskiden birkaç
    // saniye giriş yapılmamış ekran görünüyor, kullanıcı girişin olmadığını sanıyordu). İşaret userService'te konur.
    const [authRedirectPending, setAuthRedirectPending] = useState<boolean>(() => {
        try {
            const at = Number(sessionStorage.getItem(AUTH_REDIRECT_KEY) || 0);
            return at > 0 && Date.now() - at < 10 * 60 * 1000;
        } catch { return false; }
    });
    useEffect(() => {
        if (!authRedirectPending) return;
        const t = setTimeout(() => setAuthRedirectPending(false), 20000); // takılırsa kullanıcı ekranda kalmasın
        return () => clearTimeout(t);
    }, [authRedirectPending]);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [activeNotification, setActiveNotification] = useState<{ title: string; body: string; url: string } | null>(null);
    const [autoStartCezeriTopic, setAutoStartCezeriTopic] = useState<string | null>(null);
    const [autoStartCezeriSubject, setAutoStartCezeriSubject] = useState<SubjectType | null>(null);
    const [autoStartCezeriPrompt, setAutoStartCezeriPrompt] = useState<string | null>(null);
    const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
    const readPostponesLeft = () => {
        const f = (window as any).kpssUpdatePostponesLeft;
        return typeof f === 'function' ? f() : 3;
    };
    const [updatePostponesLeft, setUpdatePostponesLeft] = useState<number>(readPostponesLeft);

    // Yeni Service Worker sürüm bildirimi dinleyicisi (Kullanıcıya sorarak güncelleme)
    useEffect(() => {
        const checkPendingUpdate = () => {
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistration().then(reg => {
                    // Açılışta index.html bekleyen sürümü sormadan devreye alır (sayfa zaten yeni): burada sorma
                    const silentLaunch = typeof (window as any).kpssSilentLaunchUpdate === 'function' && (window as any).kpssSilentLaunchUpdate();
                    if (reg && reg.waiting && navigator.serviceWorker.controller && !(window as any).kpssUserRequestedUpdate && !silentLaunch) {
                        (window as any).kpssUpdateAvailable = true;
                        setUpdatePostponesLeft(readPostponesLeft());
                        setShowUpdatePrompt(true);
                    }
                }).catch(() => {});
            }
        };

        const handleUpdateReady = () => {
            if ((window as any).kpssUserRequestedUpdate) return;
            setUpdatePostponesLeft(readPostponesLeft());
            setShowUpdatePrompt(true);
        };

        const handleUpdateCleared = () => setShowUpdatePrompt(false);
        window.addEventListener('kpss_app_update_ready', handleUpdateReady);
        window.addEventListener('kpss_app_update_cleared', handleUpdateCleared);
        
        // İlk açılışta veya sekmeye geri dönüldüğünde bekleyen güncelleme var mı kontrol et
        checkPendingUpdate();
        if ((window as any).kpssUpdateAvailable && !(window as any).kpssUserRequestedUpdate) {
            setShowUpdatePrompt(true);
        }

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                checkPendingUpdate();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            window.removeEventListener('kpss_app_update_ready', handleUpdateReady);
            window.removeEventListener('kpss_app_update_cleared', handleUpdateCleared);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, []);

    // İlk yükleme kontrolü
    const isFirstMount = useRef(true);

    // Capacitor Native Platform Başlatma (iOS/Android)
    useEffect(() => {
        if (isNativePlatform()) {
            configureStatusBar();
            initNativePushNotifications();
            console.log('[App] Capacitor native platform başlatıldı.');
        }
    }, []);

    // Uygulamaya giren herkese (misafir/giriş yapmış) bildirim iznini sor ve aboneliği başlat
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const nativeFcmToken = params.get('native_fcm_token');
        const paymentStatus = params.get('payment');

        if (paymentStatus === 'success') {
            alert('🎉 Tebrikler! Shopier ödemeniz başarıyla onaylandı ve PRO Üyeliğiniz saniyeler içinde hesabınıza tanımlandı!');
            params.delete('payment');
            params.delete('package');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        } else if (paymentStatus === 'failed' || paymentStatus === 'error') {
            alert('⚠️ Ödeme işlemi tamamlanamadı veya iptal edildi. Lütfen tekrar deneyin.');
            params.delete('payment');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        }

        if (nativeFcmToken) {
            console.log("[App] Native FCM Token found in URL query:", nativeFcmToken);
            // Native TWA token'ını işaretle: backend Chrome web push token'ı yerine bunu seçer
            // (web push Samsung'da Chrome uyurken gecikir; native yol Play Hizmetleri ile anında gelir)
            try { localStorage.setItem('kpss_native_twa_token', nativeFcmToken); } catch {}
            saveSubscriptionToFirestore(nativeFcmToken)
                .then(() => console.log("[App] Native FCM Token processed."))
                .catch(e => console.error("Error processing native token:", e));
            
            // URL'deki parametreyi temizle
            params.delete('native_fcm_token');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        }

        configureStatusBar().catch(() => {});
        if (isNativePlatform()) {
            initNativePushNotifications().catch(err => console.warn("[App] initNativePushNotifications error:", err));
        } else {
            syncPushSubscription().catch(err => console.warn("[App] syncPushSubscription error:", err));
        }
    }, []);

    // Online/Offline dinleyicisi ve Başlangıç Durum Bildirimi
    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Uygulama içi (foreground) canlı bildirim dinleyicisi
    useEffect(() => {
        if (!isFirebaseActive) return;

        let unsubscribeNotifications = () => {};
        const appStartTime = Date.now();

        const unsubAuth = onAuthStateChanged(auth, (user) => {
            unsubscribeNotifications();
            setActiveNotification(null);
            // Sentry hatalarını kullanıcıya bağla (yalnızca UID; e-posta/isim gönderilmez)
            Sentry.setUser(user ? { id: user.uid } : null);

            if (user) {
                // Mağaza aboneliğini (App Store / Google Play) günde bir kez sessizce hesaba bağla
                syncStorePurchasesSilently().catch(e => console.warn("[App] Mağaza senkronu başarısız:", e));

                // local'de kalan FCM veya native push token'ı varsa Firestore'a yükle
                const cachedToken = localStorage.getItem('kpss_native_push_token') || localStorage.getItem('kpss_fcm_token');
                if (cachedToken) {
                    saveSubscriptionToFirestore(cachedToken)
                        .then(() => console.log("[App] Cached FCM/Native token uploaded to Firestore for logged-in user."))
                        .catch(e => console.error("[App] Failed to upload cached FCM token:", e));
                }
                // Android TWA native token'ı ayrıca yüklenir: web push token'ı kpss_fcm_token'ın üzerine yazmış olabilir
                const nativeTwaToken = localStorage.getItem('kpss_native_twa_token');
                if (nativeTwaToken && nativeTwaToken !== cachedToken) {
                    saveSubscriptionToFirestore(nativeTwaToken)
                        .then(() => console.log("[App] Native TWA token uploaded to Firestore."))
                        .catch(e => console.error("[App] Failed to upload native TWA token:", e));
                }

                // Bildirimler: kullanıcıya özel kutu (users/{uid}/inbox) + herkese duyurular (announcements).
                // Eskiden tek `notifications` koleksiyonundan "son 5" çekilip istemcide süzülüyordu; kullanıcı sayısı
                // arttıkça kişiye özel kayıtlar duyuruyu pencerenin dışına itiyordu.
                const listenToasts = (q: Query) => {
                    let isInitial = true;
                    return onSnapshot(q, (snapshot) => {
                        const wasInitial = isInitial;
                        isInitial = false;

                        snapshot.docChanges().forEach((change) => {
                            if (change.type === "added") {
                                // İlk yüklemedeki eski bildirimleri es geçip, sadece sonradan eklenenleri göster
                                if (wasInitial) return;

                                const data = change.doc.data();
                                setActiveNotification({
                                    title: data.title || '',
                                    body: data.body || '',
                                    url: data.url || '/'
                                });
                            }
                        });
                    }, (error) => {
                        console.warn("[App] Notification listener error:", error);
                    });
                };
                const unsubInbox = listenToasts(query(collection(db, "users", user.uid, "inbox"), orderBy("sentAt", "desc"), firestoreLimit(5)));
                const unsubAnnouncements = listenToasts(query(collection(db, "announcements"), orderBy("sentAt", "desc"), firestoreLimit(3)));
                unsubscribeNotifications = () => {
                    unsubInbox();
                    unsubAnnouncements();
                };
            }
        });

        return () => {
            unsubAuth();
            unsubscribeNotifications();
        };
    }, []);

    // Bildirimin otomatik kapanması (7 saniye)
    useEffect(() => {
        if (activeNotification) {
            const timer = setTimeout(() => {
                setActiveNotification(null);
            }, 7000);
            return () => clearTimeout(timer);
        }
    }, [activeNotification]);

    // Kullanıcı çevrimiçi durumunu (Presence) Realtime Database status/{userId} altında takip et
    useEffect(() => {
        if (!isFirebaseActive || !rtdb) return;

        let presenceUnsubscribe = () => {};

        const unsubAuth = onAuthStateChanged(auth, (user) => {
            presenceUnsubscribe();

            if (user) {
                const connectedRef = dbRef(rtdb, '.info/connected');
                const statusRef = dbRef(rtdb, `status/${user.uid}`);

                const unsubConnected = dbOnValue(connectedRef, (snap) => {
                    if (snap.val() === true) {
                        const userStatus = {
                            online: true,
                            name: user.displayName || user.email?.split('@')[0] || 'Öğrenci',
                            lastActive: dbServerTimestamp()
                        };

                        const disconnectRef = dbOnDisconnect(statusRef);
                        disconnectRef.set({
                            online: false,
                            name: user.displayName || user.email?.split('@')[0] || 'Öğrenci',
                            lastActive: dbServerTimestamp()
                        }).then(() => {
                            dbSet(statusRef, userStatus);
                        });
                    }
                });

                presenceUnsubscribe = () => {
                    unsubConnected();
                };
            }
        });

        return () => {
            unsubAuth();
            presenceUnsubscribe();
        };
    }, []);

    // URL'den gelen 'subject' veya 'game' veya 'joinRoom' parametresine göre dersi otomatik seçme
    useEffect(() => {
        if (!isAuthReady) return;

        const params = new URLSearchParams(window.location.search);
        const urlSubject = params.get('subject');
        const game = params.get('game');
        const joinRoom = params.get('joinRoom');

        const validSubjects = ['geography', 'history', 'citizenship', 'turkish', 'math'];
        if (urlSubject && validSubjects.includes(urlSubject)) {
            setSelectedSubject(urlSubject as any);
            params.delete('subject');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
            window.history.replaceState({}, document.title, newUrl);
        } else if (joinRoom) {
            // Fetch room subject from RTDB
            if (isFirebaseActive) {
                import('./services/firebase').then(({ rtdb }) => {
                    if (rtdb) {
                        import('firebase/database').then(({ ref, onValue }) => {
                            const roomDbRef = ref(rtdb, `rooms/${joinRoom}`);
                            onValue(roomDbRef, (snapshot) => {
                                const data = snapshot.val();
                                if (data && data.subject) {
                                    setSelectedSubject(data.subject);
                                } else {
                                    setSelectedSubject('geography');
                                }
                            }, (error) => {
                                console.error("[App] Failed to fetch room subject:", error);
                                setSelectedSubject('geography');
                            }, { onlyOnce: true });
                        });
                    } else {
                        setSelectedSubject('geography');
                    }
                }).catch(() => {
                    setSelectedSubject('geography');
                });
            } else {
                setSelectedSubject('geography');
            }
        } else if (game) {
            // Eğer URL'de bir oyun/modal tetiklenmişse ama ders seçilmemişse varsayılan olarak 'geography' (Coğrafya) seçelim.
            // Bu sayede MainApp yüklenir ve parametreyi okuyup ilgili oyunu/modalı açabilir.
            setSelectedSubject('geography');
        }
    }, [isAuthReady]);

    useEffect(() => {
        let isMounted = true;
        let unsubscribeAuth: any = () => {};
        let unsubscribeData: any = () => {}; 
        let authResolved = false; // Closure-safe flag

        const setupAuth = () => {
            if (!isMounted) return;

            // --- 1. AUTH DİNLEYİCİSİNİ BAŞLAT (HEMEN) ---
            if (isFirebaseActive) {
                unsubscribeAuth = onAuthStateChanged(auth, (user) => {
                    if (!isMounted) return;
                    if (user) {
                        setUser(user);
                        localStorage.setItem('kpss_is_logged_in', 'true');
                        try {
                            localStorage.setItem('kpss_cached_user', JSON.stringify({
                                uid: user.uid,
                                displayName: user.displayName || 'Öğrenci',
                                email: user.email || '',
                                photoURL: user.photoURL || null
                            }));
                        } catch (e) {}
                        setIsLoggedInCached(true);
                        const cachedFcmToken = localStorage.getItem('kpss_fcm_token');
                        if (cachedFcmToken) {
                            saveSubscriptionToFirestore(cachedFcmToken).catch(err => console.warn('[App] Cached token sync error:', err));
                        }
                    } else {
                        // YARIŞI ÖNLE: localStorage'da aktif login flag'i varsa
                        // bu, login handler'ın henüz setUser yapmış ama
                        // onAuthStateChanged'ın gecikmeli null göndermesi durumudur.
                        // Ekranı sıfırlama, login handler state'i yönetsin.
                        const stillLoggedInFlag = localStorage.getItem('kpss_is_logged_in');
                        if (stillLoggedInFlag === 'true') {
                            console.log('🔍 [App] onAuthStateChanged null geldi ama localStorage login flag aktif — state sıfırlanmıyor (race condition koruması).');
                            // 3 saniye sonra tekrar kontrol et, gerçekten logout mu?
                            setTimeout(() => {
                                if (!isMounted) return;
                                const currentAuthUser = auth.currentUser;
                                if (!currentAuthUser && localStorage.getItem('kpss_is_logged_in') !== 'true') {
                                    console.log('🔍 [App] 3sn sonra hala auth yok, gerçek logout — state sıfırlanıyor.');
                                    localStorage.removeItem('kpss_is_logged_in');
                                    localStorage.removeItem('kpss_cached_user');
                                    setIsLoggedInCached(false);
                                    setSelectedSubject(null);
                                    setUser(null);
                                }
                            }, 3000);
                        } else {
                            localStorage.removeItem('kpss_is_logged_in');
                            localStorage.removeItem('kpss_cached_user');
                            setIsLoggedInCached(false);
                            setSelectedSubject(null);
                            setUser(null);
                        }
                    }
                    if (typeof unsubscribeData === 'function') {
                        unsubscribeData();
                        unsubscribeData = () => {};
                    }
                    unsubscribeData = initUserListener(user);
                    authResolved = true;
                    setIsAuthReady(true);
                });

                // --- 2. REDIRECT SONUCUNU ARKA PLANDA KONTROL ET (Sayfa yüklenmesini bloke etmeden) ---
                handleRedirectResult().catch((err) => {
                    console.warn('Redirect kontrol hatası:', err);
                }).finally(() => {
                    try { sessionStorage.removeItem(AUTH_REDIRECT_KEY); } catch { /* yoksay */ }
                    setAuthRedirectPending(false);
                });
            } else {
                unsubscribeAuth = auth.onAuthStateChanged((user: any) => {
                    if (!isMounted) return;
                    setUser(user);
                    if (user) {
                        localStorage.setItem('kpss_is_logged_in', 'true');
                        setIsLoggedInCached(true);
                    } else {
                        localStorage.removeItem('kpss_is_logged_in');
                        setIsLoggedInCached(false);
                        setSelectedSubject(null);
                    }
                    unsubscribeData = initUserListener(user);
                    authResolved = true;
                    setIsAuthReady(true);
                });
            }
        };

        setupAuth();

        // Güvenlik Önlemi: Auth çok uzun sürerse uygulamayı aç (Maksimum 1.2 sn)
        const waitTime = (!navigator.onLine || !isFirebaseActive) ? 500 : 1200;
        const timeout = setTimeout(() => {
            if (!authResolved && isMounted) {
                console.warn('Auth yanıt vermedi, uygulama açılıyor.');
                setIsAuthReady(true);
            }
        }, waitTime);

        return () => {
            isMounted = false;
            if (typeof unsubscribeAuth === 'function') unsubscribeAuth();
            if (typeof unsubscribeData === 'function') unsubscribeData();
            clearTimeout(timeout);
        };
    }, []);

    const handleBackToMenu = useCallback((cezeriData?: any) => {
        console.log("🔄 [App] handleBackToMenu tetiklendi. Parametre:", cezeriData);
        setShowExitConfirmModal(false);
        showExitConfirmModalRef.current = false;
        selectedSubjectRef.current = null;
        setSelectedSubject(null);
        localStorage.removeItem('kpss_last_selected_subject');
        try {
            if (typeof window !== 'undefined' && window.history) {
                window.history.pushState({ page: 'kpss_root' }, '');
            }
        } catch {}
        if (cezeriData && typeof cezeriData === 'object' && cezeriData.topic) {
            console.log(`🔄 [App] Otomatik Cezeri başlatma set ediliyor: Konu="${cezeriData.topic}", Ders="${cezeriData.subject}"`);
            setAutoStartCezeriTopic(cezeriData.topic);
            setAutoStartCezeriSubject(cezeriData.subject || null);
        } else if (typeof cezeriData === 'string') {
            console.log(`🔄 [App] Otomatik Cezeri başlatma set ediliyor (Sadece konu): "${cezeriData}"`);
            setAutoStartCezeriTopic(cezeriData);
            setAutoStartCezeriSubject(null);
        } else {
            console.log("🔄 [App] Otomatik Cezeri başlatma değerleri sıfırlanıyor.");
            setAutoStartCezeriTopic(null);
            setAutoStartCezeriSubject(null);
        }
    }, []);

    const handleSelectSubject = (subject: SubjectType) => {
        setShowExitConfirmModal(false);
        showExitConfirmModalRef.current = false;
        selectedSubjectRef.current = subject;
        setSelectedSubject(subject);
        localStorage.setItem('kpss_last_selected_subject', subject);
        try {
            if (typeof window !== 'undefined' && window.history) {
                window.history.pushState({ page: 'kpss_subject', subject }, '');
            }
        } catch {}
    };

    const handleConfirmExit = useCallback(() => {
        setShowExitConfirmModal(false);
        try {
            const CapApp = (window as any).Capacitor?.Plugins?.App;
            if (CapApp && typeof CapApp.exitApp === 'function') {
                CapApp.exitApp();
                return;
            }
        } catch {}
        if (typeof window !== 'undefined') {
            try { window.close(); } catch {}
        }
    }, []);

    // ════════════════════════════════════════════════════════════════════════════
    // 🔙 EVRENSEL MOBİL GERİ YÖNETİMİ & ÇIKIŞ KONTROLÜ (Android & Apple iOS)
    // ════════════════════════════════════════════════════════════════════════════
    const handleGlobalBack = useCallback((isPopState = false) => {
        const now = Date.now();
        // 0. Debounce: 260ms içinde donanım çift tetiklenmesini engelle
        if (now - lastBackActionTimeRef.current < 260) {
            return;
        }
        lastBackActionTimeRef.current = now;

        // 1. Eğer Çıkış Onay Modalı açıksa, geri tuşuyla modalı kapatıp uygulamada kal
        if (showExitConfirmModalRef.current) {
            setShowExitConfirmModal(false);
            return;
        }

        // 2. Önce açık olan alt modallar, mini oyunlar ve çekmeceleri kapatmayı dene
        if (consumeBack()) {
            return;
        }

        // 3. Bir dersin içindeysek (selectedSubject !== null) ve açık modal yoksa:
        // KESİNLİKLE UYGULAMADAN ÇIKMA! Ders Seçim Ekranı'na dön!
        if (selectedSubjectRef.current) {
            handleBackToMenu();
            return;
        }

        // 4. Alt tam ekran sayfalar açıksa onları kapat
        if (kimBilir) { closeKimBilir(); return; }
        if (booklet) { setBooklet(false); return; }
        if (studyPlan) { setStudyPlan(false); return; }

        // 5. Kök ekrandayız (Ders Seçim Ekranı ve açık modal yok):
        // Apple iOS Kuralı: HIG Guideline 2.5.4 gereği uygulamayı kodla zorla kapatmak yasaktır
        if (isIOS()) {
            setBackToast('Zaten ders seçimi ekranındasınız');
            setTimeout(() => setBackToast(null), 2000);
            return;
        }

        // 6. Android & Web: Ders seçim ekranında direkt çıkış YOK! Onay/uyarı modalını göster!
        setShowExitConfirmModal(true);
    }, [handleBackToMenu, kimBilir, booklet, studyPlan]);

    // Sol kenardan sağa kaydırma jesti (iOS & Android Edge Swipe-Back)
    const swipeState = useEdgeSwipeBack(() => handleGlobalBack(false));

    useEffect(() => {
        try {
            window.history.pushState({ page: 'kpss_root' }, '');
        } catch {}

        const onPopState = () => {
            // Android Chrome/TWA'da history tükenmesin diye her pop'ta dummy state tazele
            try {
                window.history.pushState({ page: 'kpss_root' }, '');
            } catch {}
            handleGlobalBack(true);
        };
        const onBackButton = () => handleGlobalBack(false);

        window.addEventListener('popstate', onPopState);
        document.addEventListener('backbutton', onBackButton);

        let capListenerHandle: any = null;
        try {
            const CapApp = (window as any).Capacitor?.Plugins?.App;
            if (CapApp && typeof CapApp.addListener === 'function') {
                CapApp.addListener('backButton', () => {
                    handleGlobalBack(false);
                }).then((h: any) => {
                    capListenerHandle = h;
                }).catch(() => {});
            }
        } catch {}

        return () => {
            window.removeEventListener('popstate', onPopState);
            document.removeEventListener('backbutton', onBackButton);
            if (capListenerHandle && typeof capListenerHandle.remove === 'function') {
                capListenerHandle.remove();
            }
        };
    }, [handleGlobalBack]);

    // Çalışma planı: uygulamada geçen süre sayacı + Kapı Notları/podcast konu sinyalleri (giriş yapılınca bir kez)
    useEffect(() => {
        if (!user) return;
        installStudySignals();
        startAppTimeTracker();
        startPomodoroClock();
    }, [user]);
    // Hatırlatma bildirimi ?plan=1 ile açar
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            if (!params.has('plan')) return;
            params.delete('plan');
            window.history.replaceState(window.history.state, '', window.location.pathname + (params.toString() ? '?' + params : ''));
            setSelectedSubject(null);
            localStorage.removeItem('kpss_last_selected_subject');
            setStudyPlan(true);
        } catch { /* yok */ }
    }, []);

    // Mini podcast oynatıcısına basıldı, ders seçim ekranındayız: podcast'in dersine gir, MainApp açılınca pencereyi açar
    useEffect(() => {
        const onOpen = (e: Event) => {
            const subj = (e as CustomEvent).detail?.subject as SubjectType | undefined;
            if (selectedSubject || !subj) return; // ders içindeyken MainApp kendisi açar
            try { sessionStorage.setItem('kpss_open_podcast', '1'); } catch { /* yok */ }
            handleSelectSubject(subj);
        };
        window.addEventListener('kpss-open-podcast', onOpen);
        return () => window.removeEventListener('kpss-open-podcast', onOpen);
    }, [selectedSubject]); // eslint-disable-line react-hooks/exhaustive-deps

    // Plan görevinin adımı: ilgili derse girip özelliği açar (MainApp bayrağı okur); Stüdyo/kitapçık ders seçim tarafında
    const openPlanTarget = (t: PlanTarget) => {
        setStudyPlan(false);
        // Ücretsiz planda görünen PRO adımı (Stüdyo, Hata Onarım, 6 benzer): hakkı yok, PRO ekranı açılır
        if ((t.kind === 'studio' || (t.kind === 'subject' && t.pro)) && !isPlanPro()) {
            requestProScreen(t.kind === 'studio' ? 'Kapı Stüdyo PRO\'ya özel' : t.open === 'repair' ? 'Hata Onarım denemesi PRO\'ya özel' : '"6 benzer" sorular PRO\'ya özel');
            return;
        }
        if (t.kind === 'booklet') { setBooklet(true); return; }
        if (t.kind === 'kimbilir') {
            // Dersin Kim Bilir'i / kuş yarışı ön ayarı (KimBilirScreen ilk açılışta okur)
            try { if (t.category || t.bird) sessionStorage.setItem('kpss_kb_preset', JSON.stringify({ category: t.category, style: t.bird ? 'bird' : undefined })); } catch { /* yok */ }
            openKimBilir();
            return;
        }
        // Cezeri koç: ana menüdeki SESLİ Cezeri konuyla başlar (Vatandaşlık şemalarındaki "Cezeri AI" düğmesi gibi)
        if (t.kind === 'subject' && t.open === 'cezeri') {
            const tp = t.topic || '';
            setAutoStartCezeriPrompt(t.mode === 'warmup'
                ? `Isınalım: "${tp}" konusunu geçen derste çalıştım. Özeti kısa tut, bana bu konudan tek tek 3 soru sor.`
                : t.mode === 'quiz'
                ? `"${tp}" konusunu bugün çalıştım. Can alıcı noktaları kısaca özetle, sonra arkadaşımla çalışır gibi soru-cevap yapalım.`
                : `"${tp}" konusunu tekrar ediyorum. Önemli noktaları hatırlat, sonra soru-cevap yapalım.`);
            setAutoStartCezeriTopic(tp);
            setAutoStartCezeriSubject(t.subject);
            if (selectedSubject) handleBackToMenu({ topic: tp, subject: t.subject });
            return;
        }
        if (t.kind === 'studio') { try { sessionStorage.setItem('kpss_open_studio', '1'); } catch { /* yok */ } return; }
        try { sessionStorage.setItem('kpss_open_feature', JSON.stringify({ open: t.open, topic: t.topic, game: t.game, layer: t.layer, mode: t.mode })); } catch { /* yok */ }
        handleSelectSubject(t.subject);
    };
    // Pomodoro / mola uyarısındaki "10 dk oyun": derste değilsek (MainApp yakalamadıysa) derse girip oyunu açar
    const openPlanTargetRef = useRef(openPlanTarget);
    openPlanTargetRef.current = openPlanTarget;
    useEffect(() => {
        const h = (e: Event) => {
            const d = ((e as CustomEvent).detail || {}) as { subject?: SubjectType; handled?: boolean };
            window.setTimeout(() => { if (!d.handled && d.subject) openPlanTargetRef.current({ kind: 'subject', subject: d.subject, open: 'games', game: 'break' }); }, 0);
        };
        window.addEventListener('kpss-pomodoro-game', h);
        return () => window.removeEventListener('kpss-pomodoro-game', h);
    }, []);

    return (
        <>
            {/* Çevrimdışı Uyarısı */}
            {isOffline && (
                <div className="fixed top-0 left-0 w-full bg-red-600 text-white text-[10px] font-bold text-center py-1 z-[9999] animate-pulse">
                    ⚠️ İNTERNET YOK: Bazı özellikler (Yapay Zeka) çalışmayabilir.
                </div>
            )}

            {/* Çıkmak İçin Tekrar Basın Uyarısı (Double Back Toast) */}
            {backToast && (
                <div className="fixed bottom-12 left-1/2 -translate-x-1/2 z-[999999] bg-stone-900/95 text-amber-300 border border-amber-400/60 shadow-[0_10px_30px_rgba(0,0,0,0.8)] px-5 py-2 rounded-full text-xs font-black tracking-wide animate-bounce pointer-events-none flex items-center gap-2 backdrop-blur-md">
                    <span>🚪</span>
                    <span>{backToast}</span>
                </div>
            )}

            {/* 📱 Ekran Kenarı Kaydırma (Swipe-Back) Görsel Göstergesi (iOS & Android) */}
            {swipeState.active && swipeState.progress > 0.05 && (
                <div 
                    className="fixed z-[9999999] pointer-events-none transition-transform duration-75 flex items-center justify-center select-none"
                    style={{
                        left: `${Math.min(28, swipeState.progress * 28)}px`,
                        top: `${swipeState.y}px`,
                        transform: 'translateY(-50%)',
                        opacity: Math.min(1, swipeState.progress * 1.5)
                    }}
                >
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-xl border transition-all duration-150 ${
                        swipeState.reached 
                            ? 'bg-amber-500 text-slate-950 border-amber-300 scale-110 shadow-[0_0_25px_rgba(245,158,11,0.7)]' 
                            : 'bg-slate-900/90 text-amber-300 border-amber-500/30 shadow-2xl scale-95'
                    }`}>
                        <svg className={`w-5 h-5 transition-transform ${swipeState.reached ? 'scale-125' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </div>
                </div>
            )}

            {/* Premium Uygulama İçi Canlı Bildirim Banner'ı */}
            {activeNotification && (
                <div 
                    className="fixed left-1/2 -translate-x-1/2 z-[10000] w-[92%] max-w-sm bg-slate-900/90 backdrop-blur-md border border-white/10 rounded-2xl p-4 shadow-2xl flex items-start gap-3 animate-toast-in pointer-events-auto cursor-pointer"
                    style={{ top: 'calc(var(--sat) + 72px)' }}
                    onClick={() => {
                        const url = activeNotification.url;
                        setActiveNotification(null);
                        if (url && url !== '/') {
                            if (url.startsWith('http://') || url.startsWith('https://')) {
                                window.open(url, '_blank');
                            } else {
                                window.location.href = url;
                            }
                        }
                    }}
                >
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-xl flex-shrink-0 border border-blue-500/30">
                        🔔
                    </div>
                    <div className="flex-1 text-left">
                        <h4 className="text-white font-black text-xs leading-tight tracking-wide">{activeNotification.title}</h4>
                        <p className="text-gray-300 text-[11px] leading-snug mt-1 font-medium">{activeNotification.body}</p>
                        {activeNotification.url && activeNotification.url !== '/' && (
                            <span className="text-[10px] text-blue-400 font-bold mt-2 inline-flex items-center gap-0.5 hover:text-blue-300">
                                Detayları Gör ➔
                            </span>
                        )}
                    </div>
                    <button 
                        onClick={(e) => {
                            e.stopPropagation();
                            setActiveNotification(null);
                        }}
                        className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/5 transition"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>
            )}

            {/* Güncelleme Onay Modalı (Kullanıcı İzni İsteyen Yeni Sürüm Bildirimi) */}
            {showUpdatePrompt && (
                <div className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in pointer-events-auto">
                    <div className="bg-slate-900 border-2 border-amber-500/50 rounded-3xl max-w-sm w-full p-6 text-center text-white shadow-2xl relative overflow-hidden animate-scale-up">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-500/20 to-orange-500/30 border border-amber-500/40 flex items-center justify-center mx-auto mb-4 text-3xl shadow-inner">
                            🚀
                        </div>
                        
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-950/80 px-3 py-1 rounded-full border border-amber-500/40 inline-block mb-2">
                            YENİ SÜRÜM YAYINDA • v2.4.2
                        </span>
                        
                        <h3 className="text-lg font-black text-white mb-2">
                            Uygulamayı Güncellemek İster Misiniz?
                        </h3>
                        
                        <p className="text-xs text-gray-300 leading-relaxed mb-6">
                            Yeni KPSS soru analiz geliştirmeleri ve sistem iyileştirmeleri hazırlandı. Şimdi güncellemek ister misiniz?
                        </p>
                        
                        <div className="flex flex-col gap-2.5">
                            <button
                                onClick={() => {
                                    setShowUpdatePrompt(false);
                                    if (typeof (window as any).kpssApplyUpdate === 'function') {
                                        (window as any).kpssApplyUpdate();
                                    } else {
                                        window.location.reload();
                                    }
                                }}
                                className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-950 font-black text-sm rounded-xl shadow-lg shadow-amber-500/20 active:scale-[0.98] transition flex items-center justify-center gap-2 cursor-pointer"
                            >
                                <span>⚡</span> Şimdi Güncelle
                            </button>
                            
                            <button
                                onClick={() => {
                                    setShowUpdatePrompt(false);
                                    // index.html › kpssPostponeUpdate: 3. ertelemeden sonra ilk açılışta otomatik güncellenir
                                    const w = window as any;
                                    const left = typeof w.kpssPostponeUpdate === 'function' ? w.kpssPostponeUpdate() : 1;
                                    setUpdatePostponesLeft(left);
                                }}
                                className="w-full py-2.5 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white font-bold text-xs rounded-xl border border-white/10 transition cursor-pointer"
                            >
                                {updatePostponesLeft <= 1
                                    ? 'Ertele (sonraki açılışta otomatik güncellenecek)'
                                    : `Ertele (${updatePostponesLeft - 1} erteleme hakkı kalacak)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Özel Tema Bildirim Modalı */}
            <CustomAlertModal />

            {/* 🚪 Uygulamadan Çıkış Onay Modalı (Ders Seçim Ekranı Geri Tuşu Uyarısı) */}
            {showExitConfirmModal && (
                <div 
                    className="fixed inset-0 z-[999999] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in pointer-events-auto"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowExitConfirmModal(false);
                        }
                    }}
                >
                    <div 
                        className="bg-slate-900 border border-amber-500/40 rounded-3xl p-6 max-w-sm w-full shadow-[0_0_50px_rgba(245,158,11,0.25)] flex flex-col items-center text-center relative overflow-hidden animate-scale-up"
                    >
                        {/* Ortam Işığı Parıltısı */}
                        <div className="absolute -top-16 -left-16 w-32 h-32 bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-16 -right-16 w-32 h-32 bg-rose-500/20 rounded-full blur-3xl pointer-events-none" />

                        {/* İkon */}
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-tr from-amber-500/30 to-rose-500/30 border border-amber-400/40 p-0.5 shadow-xl flex items-center justify-center mb-3">
                            <span className="text-3xl">🚪</span>
                        </div>

                        {/* Rozet */}
                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 bg-amber-950/80 px-3 py-1 rounded-full border border-amber-500/30 mb-2">
                            KPSS KAPISI • ÇIKIŞ
                        </span>

                        {/* Başlık */}
                        <h3 className="text-lg font-black text-white mb-2 tracking-wide">
                            Uygulamadan Çıkılsın mı?
                        </h3>

                        {/* Açıklama */}
                        <p className="text-xs text-slate-300 leading-relaxed mb-6 font-medium">
                            Çalışmalarına kısa bir mola mı veriyorsun? İlerlemen ve çözdüğün tüm sorular bulutta güvende saklanıyor.
                        </p>

                        {/* Butonlar */}
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => setShowExitConfirmModal(false)}
                                className="flex-1 py-3 px-4 bg-slate-800/90 hover:bg-slate-700 active:scale-95 text-slate-200 text-xs font-bold rounded-2xl border border-white/10 transition-all cursor-pointer"
                            >
                                Vazgeç
                            </button>
                            <button
                                onClick={handleConfirmExit}
                                className="flex-1 py-3 px-4 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 active:scale-95 text-white text-xs font-black rounded-2xl shadow-lg shadow-rose-600/30 border border-white/10 transition-all cursor-pointer"
                            >
                                Çıkış Yap
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {authRedirectPending && (
                <div className="fixed inset-0 z-[99990] flex flex-col items-center justify-center gap-3 bg-[#0f172a] text-white text-center p-6">
                    <div className="loader" />
                    <p className="font-black text-base">Giriş yapılıyor, lütfen bekleyin…</p>
                    <p className="text-xs text-slate-400 max-w-xs">Hesabın doğrulandı, bilgilerin hazırlanıyor. Birkaç saniye sürebilir.</p>
                </div>
            )}

            {/* Sistem Durum Bildirimi (Sağ Alt - Sadece Işık) */}
            <div className="fixed bottom-3 right-3 z-[9999] pointer-events-auto group">
                <div 
                    className={`w-3 h-3 rounded-full shadow-lg transition-transform hover:scale-125 cursor-help ${isFirebaseActive ? 'bg-green-500 shadow-green-500/50 animate-pulse' : 'bg-orange-500 shadow-orange-500/50'}`}
                    title={isFirebaseActive ? 'Bulut Bağlantısı Aktif' : 'Yerel Mod Aktif'}
                />
            </div>
            
            <Suspense fallback={<ScreenFallback />}>
                {(!isAppShell() && !(user || isLoggedInCached)) ? (
                    <LandingPage onStartFreeTrial={() => setIsLoggedInCached(true)} />
                ) : kimBilir && !selectedSubject ? (
                    <KimBilirScreen joinCode={kimBilir.code} onClose={closeKimBilir} />
                ) : booklet && !selectedSubject ? (
                    <TgdBookletScreen onClose={() => setBooklet(false)} />
                ) : studyPlan && !selectedSubject ? (
                    <StudyPlanScreen onClose={() => setStudyPlan(false)} onOpenTarget={openPlanTarget} />
                ) : !selectedSubject ? (
                    <SubjectSelectionScreen
                        onOpenKimBilir={openKimBilir}
                        onOpenBooklet={() => setBooklet(true)}
                        onOpenStudyPlan={() => setStudyPlan(true)}
                        onSelectSubject={handleSelectSubject} 
                        autoStartCezeriTopic={autoStartCezeriTopic}
                        autoStartCezeriSubject={autoStartCezeriSubject}
                        autoStartCezeriPrompt={autoStartCezeriPrompt}
                        onClearAutoStartCezeri={() => {
                            setAutoStartCezeriTopic(null);
                            setAutoStartCezeriSubject(null);
                            setAutoStartCezeriPrompt(null);
                        }}
                        currentUser={user}
                        onAuthSuccess={(loggedInUser) => {
                            setUser(loggedInUser);
                            setIsLoggedInCached(true);
                            localStorage.setItem('kpss_is_logged_in', 'true');
                            try {
                                localStorage.setItem('kpss_cached_user', JSON.stringify({
                                    uid: loggedInUser.uid,
                                    displayName: loggedInUser.displayName || 'Öğrenci',
                                    email: loggedInUser.email || '',
                                    photoURL: loggedInUser.photoURL || null
                                }));
                            } catch (e) {}
                        }}
                    />
                ) : (
                    <MainApp onBack={handleBackToMenu} selectedSubject={selectedSubject} />
                )}
            </Suspense>
            {/* Podcast uygulamada gezinirken de çalar (services/podcastPlayer.ts) */}
            <PodcastMiniPlayer />
            {/* Pomodoro ve 45 dk mola uyarısı (services/pomodoro.ts) */}
            {user && <PomodoroWidget />}
        </>
    );
};

export default App;
